/**
 * Shared building blocks for the read-only to-do archetype cards (A/B/D).
 *
 * The cards live in `@lytra/frontend-common` and are mounted by Neptune and
 * Venus. They read *server* seeds from a {@link CustomerRequest}; every card renders
 * nothing when its seed is null.
 *
 * Brand (DESIGN.md): Dark Blue for dominant text, restraint — secondary detail
 * hides behind disclosure toggles. UI copy is German (Sie-form).
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  ChevronRight,
  ShieldCheck,
  User,
} from "lucide-react";
import { cn } from "../../lib/utils";
import type { CustomerRequestGroundedness } from "../SourceReview";

/** A quiet disclosure toggle ("N sources", "Source record", "Why?"). */
export function Disclosure({
  label,
  open,
  onToggle,
  children,
  dotted = false,
}: {
  label: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  /** Show the small leading dot (used for the grounding affordance). */
  dotted?: boolean;
}) {
  return (
    <div data-open={open}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary-accent transition-colors"
      >
        {dotted && (
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary/60" />
        )}
        <span>{label}</span>
        <ChevronRight
          className={cn(
            "h-3.5 w-3.5 transition-transform duration-200",
            open && "rotate-90",
          )}
        />
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

export function GroundednessBadge({
  groundedness,
}: {
  groundedness?: CustomerRequestGroundedness | string | null;
}) {
  const { t } = useTranslation();
  if (!groundedness) return null;
  // A fully grounded seed is the calm default — no badge (decoration-free).
  if (groundedness === "GROUNDED") return null;

  const config: Record<
    string,
    { label: string; className: string; icon: React.ReactNode }
  > = {
    // Kept for any always-on context, though the calm default hides it above.
    GROUNDED: {
      label: t("customerRequestCards.groundedness.grounded", "Belegt"),
      className: "border-primary-line bg-primary-subtle text-primary-strong",
      icon: <ShieldCheck className="h-3 w-3" aria-hidden />,
    },
    // Caution: the brand has no dedicated caution hue, so PARTIAL stays a quiet
    // navy-tinted neutral and leans on the alert icon to read as "verify".
    PARTIAL: {
      label: t("customerRequestCards.groundedness.partial", "Teilweise belegt"),
      className: "border-ink-faint bg-muted text-ink",
      icon: <AlertTriangle className="h-3 w-3" aria-hidden />,
    },
    UNGROUNDED: {
      label: t("customerRequestCards.groundedness.ungrounded", "Nicht belegt"),
      // Destructive token (DESIGN.md hsl(0 84.2% 60.2%)) — the only verdict
      // that uses it; NOT raw-Tailwind red.
      className: "border-destructive/30 bg-destructive/10 text-destructive",
      icon: <AlertTriangle className="h-3 w-3" aria-hidden />,
    },
    // Authoritative human content: a calm navy-tinted neutral (Dark-Blue tonal
    // scale), set apart from PARTIAL by the muted ink + person glyph.
    HUMAN_PROVIDED: {
      label: t("customerRequestCards.groundedness.human", "Vom Menschen"),
      className: "border-ink-faint bg-muted text-ink-muted",
      icon: <User className="h-3 w-3" aria-hidden />,
    },
  };

  const c = config[groundedness];
  if (!c) return null; // Unknown future verdict: render nothing (forward-compat).

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium",
        c.className,
      )}
    >
      {c.icon}
      {c.label}
    </span>
  );
}

